/**
 * IdExtractionService — submit an ID image to Azure Document Intelligence
 * and run the v3 post-process pipeline on the result.
 *
 * Pipeline (see docs/port-design/people-form-port.md §2 + §13a):
 *
 *   base64 image
 *     → POST /accounts/scan-id-document         (Function → Azure DI)
 *     → raw ScanIdResult (camelCase — matches server wire format)
 *     → splitFirstMiddle()                       (recover middle name)
 *     → postProcessAddress()                     (fix WY/WV anomalies, ZIP)
 *     → USPS validateAddress()                   (DPV-confirm only; the result is passed
 *                                                 through, never applied — Bill 2026-10-16)
 *     → titleCase()                              (last — caps → Title Case)
 *     → ExtractedIdFields (v3 shape, camelCase)
 *
 * The returned shape is consumed by `IdCapturePanel` which (a) immediately
 * patches CIP fields (ID type/number/issuer/dates) and (b) feeds the
 * demographic subset to `FieldComparisonDialog` for officer review.
 *
 * Modeled after the `useUsps` hook — wraps the `apiClient.accounts.scanIdDocument`
 * call, keeps response-shape translation in one place, and returns strictly-typed
 * output with no `any`.
 *
 * Ported from legacy SPFx `IdCapturePanel.tsx` lines 276-468 (extractFields).
 */

import { toNamedJsonDate } from '@/utils/jxDates';
import type { ApiClient } from '@retail-ops/api-client';
import type { ScanIdResult, UspsAddressResponse } from '@retail-ops/shared-types';
import { splitFirstMiddle } from '../utils/splitFirstMiddle.js';
import { sha256OfBase64 } from '../utils/fileHash.js';
import {
  postProcessAddress,
  type DirectionalAmbiguity,
  type StreetSuffixAmbiguity,
  type PostProcessResult,
} from '../utils/postProcessAddress.js';
import { titleCase } from '../utils/titleCase.js';

// ---------------------------------------------------------------------------
// Public shape
// ---------------------------------------------------------------------------

/**
 * Optional provenance context threaded to the scan endpoint so the persisted
 * dbo.DocumentExtract row is linked to the cart/person instead of landing
 * orphaned (NULL CartId/PersonRefId/CifId). All fields optional — when omitted
 * the extraction still runs, the row just isn't joinable (BACKLOG-0748 follow-on).
 */
export interface IdExtractionContext {
  cartId?: string;
  personRefId?: string;
  cifId?: string;
  officerCode?: string;
  expectedDocType?: string;
  sourceFileName?: string;
}

export interface ExtractedIdFields {
  // CIP fields — immediately patched to the form by IdCapturePanel
  /** ID type code: 0=Unknown, 1=DL, 2=StateID, 3=Passport, etc. */
  idTypeCode: string;
  idNumber: string;
  /** State abbreviation (US DL/ID) or country name (passport). */
  idIssuer: string;
  /** ISO YYYY-MM-DD. */
  idIssueDate: string;
  /** ISO YYYY-MM-DD. */
  idExpiryDate: string;

  // Demographics — sent through FieldComparisonDialog before patching
  firstName: string;
  middleName: string;
  lastName: string;
  /** ISO YYYY-MM-DD. */
  dob: string;
  addr: string;
  city: string;
  state: string;
  zip: string;
  gender: 'Male' | 'Female' | '';

  /** Per-field extraction confidence 0..1 (keyed by ExtractedIdFields field name). */
  confidence: Record<string, number>;

  /** Populated when postProcessAddress flagged the address as unparseable. */
  addressAnomaly?: { reason: string; rawContent: string };

  /** Populated when the city line starts with a directional word that could
   *  legitimately belong on the street line (e.g. "Northwest Minneapolis"). */
  directionalAmbiguity?: DirectionalAmbiguity;

  /** Populated when the city line starts with (or is smushed against) a
   *  street-suffix token (e.g. AI returned "STJACKSON" because the "ST"
   *  from "123 MAIN ST" bled into the city field). */
  streetSuffixAmbiguity?: StreetSuffixAmbiguity;

  /**
   * AI-original alternative values, capitalization-normalized (Title-Case for
   * proper nouns; uppercase preserved for codes like state). These are the
   * values the AI tool delivered BEFORE our post-processing pipeline ran.
   *
   * Per Bill 2026-04-24 mandate (memory: project_ai_post_deliver_keep_original_as_alternative):
   * "If we do post deliver logic altering what the Azure AI Tool delivers, we
   *  need to provide the original content delivered (with proper capitalization
   *  rules applied) as an alternative in case we have over thought something
   *  a bit."
   *
   * Keys map to ExtractedIdFields keys (firstName, middleName, lastName, dob,
   * addr, city, state, zip, gender, idTypeCode, idNumber, idIssuer,
   * idIssueDate, idExpiryDate). Only fields whose processed value diverges
   * from the capitalization-normalized AI original need surface a picker
   * affordance — see `aiOriginalDiverged` for the diff set.
   */
  aiOriginals: Record<string, string>;

  /** Subset of `aiOriginals` keys where the processed value differs from
   *  the AI original (after capitalization normalization). UI uses this to
   *  decide whether to show an "alternative available" affordance. */
  aiOriginalDiverged: ReadonlyArray<string>;

  /** Raw document type string from the AI ("driver license", "passport", etc.). */
  rawDocumentType?: string;

  /**
   * Clockwise quarter-turn (90/180/270) that makes the scanned image upright,
   * from Azure DI's page angle (server ScanOrientation). Absent when upright.
   */
  uprightRotationDeg?: number;

  /** USPS result — passed through so caller can show DPV badge. */
  uspsResult?: UspsAddressResponse;
}

// ---------------------------------------------------------------------------
// Internal helpers
// ---------------------------------------------------------------------------

/** Full US state name → 2-letter abbreviation (Azure DI returns full names for some states). */
const STATE_NAME_TO_ABBR: Record<string, string> = {
  alabama: 'AL', alaska: 'AK', arizona: 'AZ', arkansas: 'AR', california: 'CA',
  colorado: 'CO', connecticut: 'CT', delaware: 'DE', florida: 'FL', georgia: 'GA',
  hawaii: 'HI', idaho: 'ID', illinois: 'IL', indiana: 'IN', iowa: 'IA',
  kansas: 'KS', kentucky: 'KY', louisiana: 'LA', maine: 'ME', maryland: 'MD',
  massachusetts: 'MA', michigan: 'MI', minnesota: 'MN', mississippi: 'MS', missouri: 'MO',
  montana: 'MT', nebraska: 'NE', nevada: 'NV', 'new hampshire': 'NH', 'new jersey': 'NJ',
  'new mexico': 'NM', 'new york': 'NY', 'north carolina': 'NC', 'north dakota': 'ND', ohio: 'OH',
  oklahoma: 'OK', oregon: 'OR', pennsylvania: 'PA', 'rhode island': 'RI', 'south carolina': 'SC',
  'south dakota': 'SD', tennessee: 'TN', texas: 'TX', utah: 'UT', vermont: 'VT',
  virginia: 'VA', washington: 'WA', 'west virginia': 'WV', wisconsin: 'WI', wyoming: 'WY',
  'district of columbia': 'DC',
};

function normalizeIssuer(raw: string | undefined): string {
  const s = (raw ?? '').trim();
  if (!s) return '';
  if (s.length === 2) return s.toUpperCase();
  const abbr = STATE_NAME_TO_ABBR[s.toLowerCase()];
  return abbr ?? s;
}

/** "MM/DD/YYYY" → "YYYY-MM-DD" (passes through if already ISO or empty). */
function toIsoDate(val: string | undefined): string {
  const s = (val ?? '').trim();
  if (!s) return '';
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s;
  // Any format the shared parser reads (M/D/YYYY, MM-DD-YYYY, YYYYMMDD...) becomes ISO; unreadable stays as read.
  return toNamedJsonDate(s) || s;
}

/** Normalize AI-returned sex/gender to the form enum. */
function normalizeGender(raw: string | undefined): 'Male' | 'Female' | '' {
  const s = (raw ?? '').trim().toUpperCase();
  if (s === 'M' || s === 'MALE') return 'Male';
  if (s === 'F' || s === 'FEMALE') return 'Female';
  return '';
}

/** Parse combined "street city state zip" when AI returned it as one field. */
function parseCombinedAddress(raw: string): { street: string; city: string; state: string; zip: string } {
  const out = { street: '', city: '', state: '', zip: '' };
  const trimmed = raw.trim();
  if (!trimmed) return out;
  // "street, city, state zip"
  const commaMatch = trimmed.match(/^(.+?),\s*(.+?),?\s+([A-Z]{2})\s+(\d{5}(?:-\d{4})?)$/i);
  if (commaMatch) {
    out.street = commaMatch[1]!.trim();
    out.city = commaMatch[2]!.trim();
    out.state = commaMatch[3]!.toUpperCase();
    out.zip = commaMatch[4]!;
    return out;
  }
  // "street city state zip" (no commas)
  const spaceMatch = trimmed.match(/^(.+?)\s+([A-Z][a-zA-Z]+(?:\s+[A-Z][a-zA-Z]+)?)\s*,?\s*([A-Z]{2})\s+(\d{5}(?:-\d{4})?)$/i);
  if (spaceMatch) {
    out.street = spaceMatch[1]!.trim();
    out.city = spaceMatch[2]!.trim();
    out.state = spaceMatch[3]!.toUpperCase();
    out.zip = spaceMatch[4]!;
    return out;
  }
  out.street = trimmed;
  return out;
}

// ---------------------------------------------------------------------------
// Service
// ---------------------------------------------------------------------------

/**
 * GAP H (Bill 2026-05-28) — module-level extraction cache. autoClassifyCapture
 * and routeCapture each construct their own IdExtractionService, so a per-
 * instance cache wouldn't help. The cache is keyed by SHA-256 of the base64
 * bytes; FIFO-evicts after `MAX_CACHE_ENTRIES`. Concurrent calls for the
 * same image (autoClassify firing the request while the user's Apply triggers
 * the second pass) share the in-flight Promise so only ONE network call
 * actually hits the API.
 *
 * Before the cache, every Primary/Secondary Photo ID capture hit
 * /api/accounts/scan-id-document TWICE (3.5–4.8s each) — autoClassify +
 * actual extraction. With the cache, only one network call per unique image.
 */
const MAX_CACHE_ENTRIES = 50;
const extractionCache = new Map<string, Promise<ExtractedIdFields>>();

function cachePut(key: string, value: Promise<ExtractedIdFields>): void {
  if (extractionCache.has(key)) extractionCache.delete(key);
  extractionCache.set(key, value);
  while (extractionCache.size > MAX_CACHE_ENTRIES) {
    const firstKey = extractionCache.keys().next().value;
    if (firstKey !== undefined) extractionCache.delete(firstKey);
    else break;
  }
}

/**
 * GAP K (Bill 2026-05-28) — DI sometimes returns multi-line name values
 * (e.g. SSN card returned `lastName: "STUBBS\nVU"` for a Vietnamese surname
 * the model couldn't disambiguate). Collapse whitespace + take the FIRST
 * meaningful token block. If the AI returned multiple distinct names joined
 * by a newline, the FIRST line is statistically the most-likely surname for
 * SSN cards (line-ordering matches the printed form). Officer can still
 * type-over via the Review form.
 */
function stripNameNewlines(raw: string): string {
  if (!raw) return raw;
  if (!raw.includes('\n')) return raw.trim();
  // Take the first non-empty line; collapse internal whitespace.
  const lines = raw.split(/\r?\n/).map((s) => s.trim()).filter(Boolean);
  if (lines.length === 0) return '';
  return lines[0]!.replace(/\s+/g, ' ');
}

export class IdExtractionService {
  public constructor(private readonly apiClient: ApiClient) {}

  /**
   * Submit a base64-encoded image to Azure Doc Intelligence and return the
   * post-processed v3 shape. Throws if the Function returns `Success: false`
   * or the transport fails.
   *
   * Bill 2026-05-28: caches by SHA-256 of the base64 bytes — same image hit
   * during a session returns the cached promise instead of re-hitting the
   * API. Fixes the autoClassify + actual-extraction duplicate-call gap.
   */
  public async extractFromImage(
    base64: string,
    mimeType: string,
    signal?: AbortSignal,
    context?: IdExtractionContext,
  ): Promise<ExtractedIdFields> {
    // GAP H — try cache first. Abort signal disables cache (caller wants
    // a fresh fetch they can cancel).
    let cacheKey: string | null = null;
    if (!signal) {
      try {
        cacheKey = await sha256OfBase64(base64);
        const hit = extractionCache.get(cacheKey);
        if (hit) return hit;
      } catch {
        // Hash compute failed — fall through; behave like cache miss.
        cacheKey = null;
      }
    }
    const promise = this.doExtract(base64, mimeType, signal, context);
    if (cacheKey) cachePut(cacheKey, promise);
    return promise;
  }

  private async doExtract(
    base64: string,
    mimeType: string,
    signal?: AbortSignal,
    context?: IdExtractionContext,
  ): Promise<ExtractedIdFields> {
    // Photo-ID extraction routes to the Azure Document Intelligence
    // prebuilt-idDocument model via /accounts/scan-id-document (Bill 2026-06-13).
    //
    // Reverts the 2026-05-31 swap to Content Understanding. CU returned the
    // residential address as ONE combined, comma-stripped line with a null City
    // (verified on the MO DL sample: address="123 SAMPLE ST JEFFERSON CITY",
    // city=null, state="MISSOURI", postalCode="65105") — which the address-
    // completeness gate below then blanked entirely, so nothing auto-filled. DI
    // splits Address/City/State/PostalCode into separate fields (the proven SPFx
    // pipeline). Same ScanIdResult wire shape (DI is the canonical shape; CU was
    // built as its superset), so the post-process pipeline below + every
    // consumer are unchanged.
    const resp: ScanIdResult = await this.apiClient.accounts.scanIdDocument(
      {
        Image: base64,
        MimeType: mimeType,
        // Provenance linkage — only sends keys that are present (BE accepts
        // them case-insensitively; null/undefined are omitted).
        ...(context?.cartId ? { CartId: context.cartId } : {}),
        ...(context?.personRefId ? { PersonRefId: context.personRefId } : {}),
        ...(context?.cifId ? { CifId: context.cifId } : {}),
        ...(context?.officerCode ? { OfficerCode: context.officerCode } : {}),
        ...(context?.expectedDocType ? { ExpectedDocType: context.expectedDocType } : {}),
        ...(context?.sourceFileName ? { SourceFileName: context.sourceFileName } : {}),
      },
      signal ? { signal } : undefined,
    );

    if (!resp.success || !resp.fields) {
      throw new Error(resp.error ?? 'Content Understanding extraction failed');
    }

    const f = resp.fields;
    const confidence: Record<string, number> = {};
    const take = <T>(field: { value: T; confidence: number } | undefined, key: string): T | undefined => {
      if (!field) return undefined;
      confidence[key] = field.confidence;
      return field.value;
    };

    // ── 1. Raw field extraction (with confidence capture) ─────────────────
    const rawIdType = take(f.identificationType, 'idTypeCode') ?? '';
    const rawIssuer = take(f.issuingAuthority, 'idIssuer') ?? '';
    const rawIdNumber = take(f.idNumber, 'idNumber') ?? '';
    const rawIssueDate = take(f.issueDate, 'idIssueDate') ?? '';
    const rawExpiryDate = take(f.expirationDate, 'idExpiryDate') ?? '';

    // GAP K (Bill 2026-05-28) — DI returns multi-line names when it can't
    // disambiguate two candidate surnames (e.g. SSN card returned
    // `lastName: "STUBBS\nVU"`). Strip newlines to a single line BEFORE
    // splitFirstMiddle / titleCase so the downstream pipeline doesn't
    // produce a malformed lastName field.
    const rawFirst = stripNameNewlines(take(f.firstName, 'firstName') ?? '');
    const rawMiddle = stripNameNewlines(take(f.middleName, 'middleName') ?? '');
    const rawLast = stripNameNewlines(take(f.lastName, 'lastName') ?? '');
    const rawDob = take(f.dateOfBirth, 'dob') ?? '';
    const rawSex = take(f.sex, 'gender') ?? '';

    const rawAddress = take(f.address, 'addr') ?? '';
    const rawCity = take(f.city, 'city') ?? '';
    const rawState = take(f.state, 'state') ?? '';
    const rawPostal = take(f.postalCode, 'zip') ?? '';

    // ── 2. Name split (recover middle name packed into FirstName) ─────────
    let firstName = rawFirst;
    let middleName = rawMiddle;
    if (!middleName && firstName) {
      const split = splitFirstMiddle(firstName);
      firstName = split.first;
      middleName = split.middle;
    }

    // ── 3. Address: if combined into `addr` but city/state missing, parse ─
    let street = rawAddress;
    let city = rawCity;
    let state = rawState ? normalizeIssuer(rawState) : '';
    let zip = rawPostal;
    if (street && (!city || !state)) {
      const parsed = parseCombinedAddress(street);
      if (parsed.city) {
        street = parsed.street;
        if (!city) city = parsed.city;
        if (!state) state = parsed.state;
        if (!zip) zip = parsed.zip;
      }
    }

    // ── 4. postProcessAddress — WY swap, WV state-in-zip, ZIP format ──────
    const pp: PostProcessResult = postProcessAddress({
      streetAddress: street,
      city,
      state,
      postalCode: zip,
      rawContent: rawAddress,
    });

    let addressAnomaly: ExtractedIdFields['addressAnomaly'];
    let directionalAmbiguity: DirectionalAmbiguity | undefined;
    let addrOut = '';
    let cityOut = '';
    let stateOut = '';
    let zipOut = '';
    let uspsResult: UspsAddressResponse | undefined;

    let streetSuffixAmbiguity: StreetSuffixAmbiguity | undefined;
    if (pp.kind === 'anomaly') {
      addressAnomaly = { reason: pp.reason, rawContent: pp.rawContent };
      // Leave address fields blank; officer keys them in manually.
    } else if (pp.streetAddress && !pp.city && pp.state) {
      // Address completeness gate (Bill 2026-06-03). Core Director F9–F12
      // (street/city/state/zip) are all required for a residential address.
      // Missing city is the most common DL OCR failure — street + city print on
      // adjacent lines and the city line drops out. An incomplete address would
      // auto-fill and then fail USPS validation silently (the Widener smoke
      // test: street mis-read, city blank, zip wrong, all shown at "100%").
      // Promote to an anomaly so the officer re-scans or manually keys the city
      // instead of accepting incomplete demographics.
      addressAnomaly = {
        reason: 'address incomplete — city field missing (likely OCR parse error on the street/city line boundary)',
        rawContent: rawAddress,
      };
      // Leave address fields blank; officer keys them in manually.
    } else {
      addrOut = pp.streetAddress;
      cityOut = pp.city;
      stateOut = pp.state;
      zipOut = pp.postalCode;
      if (pp.directionalAmbiguity) directionalAmbiguity = pp.directionalAmbiguity;
      if (pp.streetSuffixAmbiguity) streetSuffixAmbiguity = pp.streetSuffixAmbiguity;

      // ── 5. USPS validate (only when we have enough to try) ────────────
      if (addrOut && stateOut) {
        try {
          const body: Record<string, unknown> = {
            streetAddress: addrOut,
            state: stateOut,
          };
          if (cityOut) body['city'] = cityOut;
          if (zipOut) body['ZIPCode'] = zipOut.split('-')[0];
          uspsResult = await this.apiClient.usps.address(
            body as never,
            signal ? { signal } : undefined,
          );
          // The USPS result is NOT applied here (Collette Cruse, 2026-10-16: "it
          // changed it without approval"; Bill approved 2026-10-16). The address
          // stays exactly as printed on the ID — including the unit, which USPS
          // returns separately as secondaryAddress and the old overwrite dropped.
          // The address section re-verifies the scanned address and shows the
          // USPS suggestion line by line; nothing changes until the officer
          // clicks "Accept Standardized".
        } catch {
          // USPS failure is non-fatal — fall back to the raw (post-processed)
          // AI address. Officer can still hit Save.
        }
      }
    }

    // ── 6. titleCase (LAST — after USPS caps) ─────────────────────────────
    const out: ExtractedIdFields = {
      idTypeCode: rawIdType,
      idNumber: rawIdNumber,
      idIssuer: rawIdType === '3' ? rawIssuer : normalizeIssuer(rawIssuer),
      idIssueDate: toIsoDate(rawIssueDate),
      idExpiryDate: toIsoDate(rawExpiryDate),

      firstName: titleCase(firstName),
      middleName: titleCase(middleName),
      lastName: titleCase(rawLast),
      dob: toIsoDate(rawDob),
      addr: titleCase(addrOut),
      city: titleCase(cityOut),
      state: stateOut, // keep 2-letter abbr uppercase
      zip: zipOut,
      gender: normalizeGender(rawSex),
      confidence,
      aiOriginals: {},
      aiOriginalDiverged: [],
    };

    // ── 7. AI-original alternative values (Bill 2026-04-24 mandate) ───────
    // Capitalize the raw AI output for presentation. State stays uppercase
    // (it's a code), other proper-noun fields go to Title Case. We compare
    // the AI original (capitalization-normalized) against our processed
    // value to compute the diverged set — UI surfaces an alternative
    // picker only on diverged fields.
    const aiOriginals: Record<string, string> = {
      firstName: titleCase(rawFirst),
      middleName: titleCase(rawMiddle),
      lastName: titleCase(rawLast),
      dob: toIsoDate(rawDob),
      addr: titleCase(rawAddress),
      city: titleCase(rawCity),
      state: rawState ? normalizeIssuer(rawState) : '',
      zip: rawPostal,
      gender: normalizeGender(rawSex),
      idTypeCode: rawIdType,
      idNumber: rawIdNumber,
      idIssuer: rawIdType === '3' ? rawIssuer : normalizeIssuer(rawIssuer),
      idIssueDate: toIsoDate(rawIssueDate),
      idExpiryDate: toIsoDate(rawExpiryDate),
    };
    out.aiOriginals = aiOriginals;
    out.aiOriginalDiverged = (
      Object.keys(aiOriginals) as ReadonlyArray<keyof typeof aiOriginals>
    ).filter((k) => {
      const processed = (out as unknown as Record<string, unknown>)[k];
      const original = aiOriginals[k];
      if (typeof processed !== 'string') return false;
      return original !== '' && original !== processed;
    });

    if (addressAnomaly) out.addressAnomaly = addressAnomaly;
    if (streetSuffixAmbiguity) {
      // Re-project ambiguity values through titleCase so the UI displays
      // clean variants regardless of how the AI cased its output.
      out.streetSuffixAmbiguity = {
        leadingWord: streetSuffixAmbiguity.leadingWord,
        smushed: streetSuffixAmbiguity.smushed,
        cityAsIs: titleCase(streetSuffixAmbiguity.cityAsIs),
        alternateCity: titleCase(streetSuffixAmbiguity.alternateCity),
        alternateAddress: titleCase(streetSuffixAmbiguity.alternateAddress),
        recommendedChoice: streetSuffixAmbiguity.recommendedChoice,
      };
    }
    if (directionalAmbiguity) {
      // Re-project the ambiguity values through titleCase so the UI gets
      // clean "Northwest Minneapolis" / "Minneapolis" / "123 Main St NW"
      // rather than the raw ALL-CAPS AI output.
      out.directionalAmbiguity = {
        leadingWord: directionalAmbiguity.leadingWord,
        cityAsIs: titleCase(directionalAmbiguity.cityAsIs),
        alternateCity: titleCase(directionalAmbiguity.alternateCity),
        alternateAddress: titleCase(directionalAmbiguity.alternateAddress),
        recommendedChoice: directionalAmbiguity.recommendedChoice,
      };
    }
    if (resp.rawDocumentType) out.rawDocumentType = resp.rawDocumentType;
    const turn = resp.geometry?.uprightRotationDeg;
    if (turn === 90 || turn === 180 || turn === 270) out.uprightRotationDeg = turn;
    if (uspsResult) out.uspsResult = uspsResult;

    return out;
  }
}
